<!-- Calendar grid dropdown content rendered inside a DatePicker. -->

<script lang="ts" setup>
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import {
  DatePickerArrow,
  DatePickerCalendar,
  DatePickerCell,
  DatePickerCellTrigger,
  DatePickerContent,
  DatePickerGrid,
  DatePickerGridBody,
  DatePickerGridHead,
  DatePickerGridRow,
  DatePickerHeadCell,
  DatePickerHeader,
  DatePickerHeading,
  DatePickerNext,
  DatePickerPrev,
} from 'reka-ui';

import Button from './Button.vue';
import * as l from './DatePickerCalendarContent.loc.js';
import calendarCss from './dropdown/DropdownContent.module.scss';

interface Props {
  /** Whether to show the "Today" button at the bottom */
  showTodayButton?: boolean;
  /** Whether the today button should be disabled */
  todayDisabled?: boolean;
  /** Today button click handler */
  onTodayClick?: () => void;
}

withDefaults(defineProps<Props>(), {
  showTodayButton: false,
});

const t = useTranslate();
</script>

<template>
  <DatePickerContent :class="[calendarCss.dropdownContent, calendarCss.calendarContent]">
    <DatePickerArrow :class="calendarCss.arrow" />

    <DatePickerCalendar
      v-slot="{ weekDays, grid }"
      :class="calendarCss.calendar"
    >
      <DatePickerHeader :class="calendarCss.calendarHeader">
        <DatePickerPrev :class="calendarCss.calendarNavButton">
          <ChevronLeft :class="css.navIcon" />
        </DatePickerPrev>
        <DatePickerHeading :class="calendarCss.calendarHeading" />
        <DatePickerNext :class="calendarCss.calendarNavButton">
          <ChevronRight :class="css.navIcon" />
        </DatePickerNext>
      </DatePickerHeader>

      <div :class="calendarCss.calendarWrapper">
        <DatePickerGrid
          v-for="month in grid"
          :key="month.value.toString()"
          :class="calendarCss.calendarGrid"
        >
          <DatePickerGridHead>
            <DatePickerGridRow :class="calendarCss.calendarRow">
              <DatePickerHeadCell
                v-for="day in weekDays"
                :key="day"
                :class="calendarCss.calendarHeadCell"
              >
                {{ day }}
              </DatePickerHeadCell>
            </DatePickerGridRow>
          </DatePickerGridHead>
          <DatePickerGridBody>
            <DatePickerGridRow
              v-for="(weekDates, index) in month.rows"
              :key="`weekDate-${index}`"
              :class="calendarCss.calendarRow"
            >
              <DatePickerCell
                v-for="weekDate in weekDates"
                :key="weekDate.toString()"
                :date="weekDate"
                :class="calendarCss.calendarCell"
              >
                <DatePickerCellTrigger
                  :day="weekDate"
                  :month="month.value"
                  :class="calendarCss.calendarCellTrigger"
                />
              </DatePickerCell>
            </DatePickerGridRow>
          </DatePickerGridBody>
        </DatePickerGrid>
      </div>
    </DatePickerCalendar>

    <!-- Today Button -->
    <div
      v-if="showTodayButton && !todayDisabled"
      :class="css.todayButtonContainer"
    >
      <Button
        variant="link"
        size="medium"
        :class="css.todayButton"
        :title="t(l.today)"
        @click="onTodayClick"
      >
        {{ t(l.today) }}
      </Button>
    </div>
  </DatePickerContent>
</template>

<style lang="scss" module="css">
.todayButtonContainer {
  display: flex;
  flex-direction: column;
  padding-top: $spacing-12;
  margin-top: $spacing-12;
  border-top: 1px solid $color-gray-100;
}

.navIcon {
  width: 18px;
  height: 18px;
}
</style>
